import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { FileClock, RefreshCw, Shield } from 'lucide-react';

export function AuditLogsPage() {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchLogs = async () => {
    try {
      const res = await api.superadmin.getAuditLogs();
      setLogs(res.auditLogs);
    } catch (err) {
      console.error('Failed to load audit logs:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchLogs();
  };

  return (
    <div className="p-8 space-y-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-white tracking-tight">Security Audit Logs</h1>
            <span className="px-2 py-0.5 text-[10px] uppercase font-bold tracking-wider rounded bg-zinc-800 text-zinc-300 border border-zinc-700">
              Immutable Trace
            </span>
          </div>
          <p className="text-xs text-zinc-400 mt-1">
            Tamper-resistant audit trails capturing administrative and security operations
          </p>
        </div>

        <button
          onClick={handleRefresh}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border border-zinc-800 transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          <span>Refresh Logs</span>
        </button>
      </div>

      {/* Audit Log Table */}
      <div className="pulse-card rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-[#161616] text-zinc-400 uppercase tracking-wider font-medium border-b border-[#222222]">
              <tr>
                <th className="py-3 px-4">Timestamp</th>
                <th className="py-3 px-4">Action</th>
                <th className="py-3 px-4">Actor</th>
                <th className="py-3 px-4">Organization</th>
                <th className="py-3 px-4">IP Address</th>
                <th className="py-3 px-4">Metadata</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1D1D1D] text-zinc-300">
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-zinc-500">
                    Loading security audit events...
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-zinc-500">
                    No audit records logged yet.
                  </td>
                </tr>
              ) : (
                logs.map((log) => {
                  let parsedMeta: any = null;
                  try {
                    if (log.metadata) parsedMeta = JSON.parse(log.metadata);
                  } catch {}

                  return (
                    <tr key={log.id} className="hover:bg-zinc-900/40 transition-colors">
                      <td className="py-3 px-4 font-mono text-zinc-400 text-[11px]">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 font-mono font-medium text-pulse-blue">
                        {log.action}
                      </td>
                      <td className="py-3 px-4 text-white">{log.actorEmail}</td>
                      <td className="py-3 px-4">
                        {log.organization ? (
                          <span className="text-zinc-200">{log.organization.name}</span>
                        ) : (
                          <span className="text-zinc-600 italic">System / Global</span>
                        )}
                      </td>
                      <td className="py-3 px-4 font-mono text-zinc-400">{log.ipAddress || '127.0.0.1'}</td>
                      <td className="py-3 px-4">
                        {parsedMeta ? (
                          <span className="font-mono text-[11px] text-zinc-400 bg-zinc-950 px-2 py-0.5 rounded border border-zinc-800">
                            {JSON.stringify(parsedMeta)}
                          </span>
                        ) : (
                          <span className="text-zinc-600">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
